import { useMemo, useState } from 'react';

import { Scale } from 'lucide-react';

import { formatarData } from '@/nucleo/formato/datas';
import { Badge } from '@/ui/base/badge';
import { DataTable } from '@/ui/DataTable';
import { DateRangeFilter } from '@/ui/DateRangeFilter';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { StatusBadge, type StatusVariant } from '@/ui/StatusBadge';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';

import { GatingBanner } from '../comum/GatingBanner';
import { useRecursos } from './dados';
import { PrazoRespostaCountdown } from './PrazoRespostaCountdown';
import {
  ROTULO_DO_TIPO_DE_RECURSO,
  TAMANHO_PAGINA_PADRAO,
  type FiltroDeRecursos,
  type RecursoAdministrativo,
  type StatusDoRecurso,
} from './contrato';

/**
 * `/credenciado/recursos` — recursos administrativos do credenciado.
 *
 * As defesas protocoladas contra multa ou outra penalidade, com o número do
 * processo, o tipo, o que está sendo contestado, a data de protocolo, o
 * prazo legal de resposta (enquanto em análise), a decisão (quando já
 * decidido) e o andamento. Dado mockado — ver `dados.ts` e `comum/mock.ts`
 * para a justificativa.
 */

/**
 * `StatusBadge` só aceita o conjunto fechado de selos de `@/ui/StatusBadge`.
 * "Deferido"/"indeferido" batem com "aprovado"/"rejeitado" quase à risca;
 * os outros dois pegam o selo de tom mais próximo — sempre com o rótulo em
 * português por fora, explícito.
 */
const SELO_DO_RECURSO: Record<StatusDoRecurso, { variante: StatusVariant; rotulo: string }> = {
  em_analise: { variante: 'em_execucao', rotulo: 'Em análise' },
  deferido: { variante: 'aprovado', rotulo: 'Deferido' },
  indeferido: { variante: 'rejeitado', rotulo: 'Indeferido' },
  em_recurso_superior: { variante: 'processado', rotulo: 'Em recurso superior' },
};

const OPCOES_DE_STATUS = [
  { value: 'em_analise', label: 'Em análise' },
  { value: 'deferido', label: 'Deferido' },
  { value: 'indeferido', label: 'Indeferido' },
  { value: 'em_recurso_superior', label: 'Em recurso superior' },
];

/**
 * Módulo, e não dentro da tela: `useTableViewPreferences` pede lista estável.
 * O número do processo identifica a linha e não se oculta; Protocolo e Status
 * explicam os filtros de mesmo nome.
 */
const COLUNAS: TableViewColumn<RecursoAdministrativo>[] = [
  {
    id: 'numero_processo',
    header: 'Número do processo',
    cell: (recurso) => <span className="font-mono text-sm">{recurso.numero_processo}</span>,
    sortKey: 'numero_processo',
    isCritical: true,
  },
  {
    id: 'tipo',
    header: 'Tipo',
    cell: (recurso) => (
      <Badge variant="outline" className="text-xs font-normal">
        {ROTULO_DO_TIPO_DE_RECURSO[recurso.tipo]}
      </Badge>
    ),
    sortKey: (recurso) => ROTULO_DO_TIPO_DE_RECURSO[recurso.tipo],
  },
  {
    id: 'referencia',
    header: 'Referência',
    cell: (recurso) => <span className="text-sm">{recurso.referencia}</span>,
  },
  {
    id: 'data_protocolo',
    header: 'Protocolo',
    cell: (recurso) => <span className="text-sm">{formatarData(recurso.data_protocolo)}</span>,
    sortKey: 'data_protocolo',
    linkedFilterKeys: ['protocolo'],
  },
  {
    id: 'prazo_resposta',
    header: 'Prazo Resposta',
    cell: (recurso) => (
      <div className="flex items-center gap-2">
        <span className="text-sm">{formatarData(recurso.prazoResposta)}</span>
        <PrazoRespostaCountdown prazoResposta={recurso.prazoResposta} status={recurso.status} />
      </div>
    ),
    sortKey: 'prazoResposta',
  },
  {
    id: 'decisao',
    header: 'Decisão',
    cell: (recurso) => (
      <span className="text-sm">
        {recurso.decisao ?? <span className="text-muted-foreground">—</span>}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (recurso) => {
      const selo = SELO_DO_RECURSO[recurso.status];
      return <StatusBadge status={selo.variante} label={selo.rotulo} />;
    },
    sortKey: 'status',
    isStatusColumn: true,
    linkedFilterKeys: ['status'],
  },
];

export function RecursosPage() {
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const [protocoloDe, setProtocoloDe] = useState('');
  const [protocoloAte, setProtocoloAte] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(TAMANHO_PAGINA_PADRAO);

  const limparFiltros = () => {
    setBusca('');
    setStatus('');
    setProtocoloDe('');
    setProtocoloAte('');
    setPagina(1);
  };

  const filtro: FiltroDeRecursos = {
    pagina,
    tamanho,
    busca: busca || undefined,
    status: (status || undefined) as StatusDoRecurso | undefined,
    protocoloDe: protocoloDe || undefined,
    protocoloAte: protocoloAte || undefined,
  };
  const { data, isLoading, isError, refetch } = useRecursos(filtro);

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Buscar',
      type: 'search',
      placeholder: 'Buscar por referência ou nº do processo…',
      ariaLabel: 'Buscar recursos',
      flex: true,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: OPCOES_DE_STATUS,
      width: 'w-full sm:w-[220px]',
    },
  ];

  const filterValues = useMemo(
    () => ({ status, protocolo: protocoloDe || protocoloAte ? 'ativo' : '' }),
    [status, protocoloDe, protocoloAte],
  );

  const tableView = useTableViewPreferences({
    contextKey: 'credenciado:recursos:list',
    columns: COLUNAS,
    filterValues,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Recursos Administrativos"
        description="Acompanhe seus recursos e as decisões da SMT."
      />

      <GatingBanner />

      {isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar os recursos agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <>
          <FilterBar
            fields={campos}
            values={{ busca, status }}
            onChange={(chave, valor) => {
              if (chave === 'busca') setBusca(valor);
              if (chave === 'status') setStatus(valor);
              setPagina(1);
            }}
            onClear={limparFiltros}
          >
            <DateRangeFilter
              label="Protocolo"
              from={protocoloDe}
              to={protocoloAte}
              onChange={({ from, to }) => {
                setProtocoloDe(from);
                setProtocoloAte(to);
                setPagina(1);
              }}
            />
          </FilterBar>

          <DataTable
            columns={tableView.orderedColumns}
            personalizacao={tableView}
            data={data?.itens ?? []}
            isLoading={isLoading}
            total={data?.total}
            page={pagina}
            perPage={tamanho}
            onPageChange={setPagina}
            onPerPageChange={(novoTamanho) => {
              setTamanho(novoTamanho);
              setPagina(1);
            }}
            emptyIcon={Scale}
            emptyTitle="Nenhum recurso encontrado"
            emptyDescription={
              busca || status || protocoloDe || protocoloAte
                ? 'Nenhum recurso está nesses filtros. Tente afrouxar a busca.'
                : 'Você não possui recursos administrativos registrados.'
            }
            minWidth="960px"
            ariaLabel="Lista de recursos administrativos"
          />
        </>
      )}
    </div>
  );
}

export default RecursosPage;
